import React, { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const STATUS_BADGE = {
  pending:   'bg-amber-50 text-amber-700',
  cleared:   'bg-emerald-50 text-emerald-700',
  paid_out:  'bg-gray-100 text-gray-500',
};

const CreatorEarnings = () => {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, pending: 0, paid: 0 });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 25;

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/api/creator/earnings?page=${page}&limit=${LIMIT}`, { headers: h })
        .then(r => r.ok ? r.json() : { items: [], total: 0 }),
      fetch(`${API_URL}/api/creator/earnings/summary`, { headers: h })
        .then(r => r.ok ? r.json() : null),
    ]).then(([list, summaryData]) => {
      setEarnings(list.items || []);
      setTotal(list.total || 0);
      if (summaryData) {
        setSummary({
          total: summaryData.lifetime_earnings?.total_cents || 0,
          pending: summaryData.pending_earnings?.total_cents || 0,
          paid: summaryData.paid_earnings?.total_cents || 0,
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page]);

  return (
    <CreatorLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6">Earnings</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total earned', value: `$${((summary.total || 0) / 100).toFixed(2)}`, color: 'emerald' },
          { label: 'Pending clearance', value: `$${((summary.pending || 0) / 100).toFixed(2)}`, color: 'amber' },
          { label: 'Paid out', value: `$${((summary.paid || 0) / 100).toFixed(2)}`, color: 'gray' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color === 'emerald' ? 'text-emerald-600' : color === 'amber' ? 'text-amber-600' : 'text-gray-600'}`}>
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : earnings.length === 0 ? (
          <div className="text-center py-16">
            <DollarSign className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No earnings yet</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-5 gap-4 px-5 py-3 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
              <div className="col-span-2">Template</div>
              <div className="text-right">Amount</div>
              <div className="text-center">Status</div>
              <div className="text-right">Date</div>
            </div>
            <div className="divide-y divide-gray-50">
              {earnings.map((e, i) => (
                <div key={e.id || i} className="grid grid-cols-1 md:grid-cols-5 gap-2 md:gap-4 px-5 py-4 items-center">
                  <div className="col-span-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{e.template_name || '—'}</p>
                  </div>
                  <p className="text-sm font-semibold text-emerald-600 md:text-right">
                    ${((e.creator_amount_cents || 0) / 100).toFixed(2)}
                  </p>
                  <div className="flex md:justify-center">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[e.status] || 'bg-gray-100 text-gray-500'}`}>
                      {e.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 md:text-right">
                    {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                  </p>
                </div>
              ))}
            </div>

            {total > LIMIT && (
              <div className="px-5 py-4 border-t border-gray-50 flex justify-end gap-2">
                {page > 1 && (
                  <button onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">
                    Previous
                  </button>
                )}
                {earnings.length === LIMIT && (
                  <button onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700">
                    Next
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </CreatorLayout>
  );
};

export default CreatorEarnings;
